import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';

import { Period } from 'dashboard/lib/unixTime';
import { ReportsMetricsSummary, ReportsOverTimeResponse } from 'dashboard/models/metrics-reports';
import {
  ReportsMetricsActions,
  ReportsMetricsActionType,
} from 'dashboard/redux/actions/metrics-reports';
import { Iso8601DateString } from 'lib/types';

const NOW = DateTime.local();
const DEFAULT_CREATED_AT_START = NOW.minus({ days: 14 }).startOf('day');
const DEFAULT_CREATED_AT_END = NOW.endOf('day');

export interface ReportsMetricsFilterParams {
  createdAtStart: Iso8601DateString;
  createdAtEnd: Iso8601DateString;
  templateIds: string[];
  tagIds: string[];
}

export type ReportTemplateToken = `rptp_${string}`;
export type ReportsGranularities = Record<ReportTemplateToken, Period>;

export interface ReportsMetricsState {
  volumeSummary: ReportsMetricsSummary[];
  reportsOverTime: ReportsOverTimeResponse;
  queriedAt?: Iso8601DateString;
  updatedAt?: Iso8601DateString;
  granularities: ReportsGranularities;
  // query filters that were the last filters sent to the server.
  lastQueryFilters?: ReportsMetricsFilterParams;
  filters: ReportsMetricsFilterParams;
}

export const initialState: ReportsMetricsState = {
  volumeSummary: [],
  reportsOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  queriedAt: undefined,
  filters: {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    createdAtStart: undefined,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    createdAtEnd: undefined,
    templateIds: [],
    tagIds: [],
  },
  lastQueryFilters: undefined,
  granularities: {},
};

// This is a function so we always get the most up-to-date created at times
const getInitialState = (): ReportsMetricsState => {
  const currentTime = DateTime.local();
  return {
    ...initialState,
    filters: {
      ...initialState.filters,
      createdAtStart: currentTime.minus({ days: 14 }).startOf('day').toISO(),
      createdAtEnd: currentTime.endOf('day').toISO(),
    },
    volumeSummary: [...initialState.volumeSummary],
    reportsOverTime: {
      ...initialState.reportsOverTime,
      domain: [
        currentTime.minus({ days: 14 }).startOf('day').toUnixInteger(),
        currentTime.endOf('day').toUnixInteger(),
      ],
    },
    granularities: {},
  };
};

export default (
  state: ReportsMetricsState = initialState,
  action: ReportsMetricsActions,
): ReportsMetricsState => {
  switch (action.type) {
    case ReportsMetricsActionType.SetReportsVolumeSummary: {
      return {
        ...state,
        volumeSummary: action.payload.volumeSummary,
        queriedAt: action.payload.queriedAt,
        updatedAt: action.payload.updatedAt,
        lastQueryFilters: {
          ...state.filters,
        },
      };
    }

    case ReportsMetricsActionType.SetReportsMetricsGranularities: {
      return {
        ...state,
        granularities: action.payload.granularities,
      };
    }

    case ReportsMetricsActionType.SetReportsOverTime: {
      return {
        ...state,
        reportsOverTime: action.payload.reportsOverTime,
        queriedAt: action.payload.queriedAt,
        lastQueryFilters: {
          ...state.filters,
        },
      };
    }

    case ReportsMetricsActionType.SetReportsMetricsFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      if (isEqual(newFilters, state.filters)) {
        return state;
      } else {
        return {
          ...state,
          filters: newFilters,
        };
      }
    }

    case ReportsMetricsActionType.ClearMetrics: {
      return { ...getInitialState(), filters: state.filters };
    }
    default:
      return state;
  }
};
